import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrg } from '../../../shared/contexts/OrgContext';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { supabase } from '../../../shared/lib/supabase';
import { videosService, Video } from '../../../shared/services/videosService';
import {
  uploadFile,
  loadUploadSession,
  clearUploadSession,
  UploadProgress,
} from '../../../shared/services/multipartUploadService';
import { videoProcessingJobsService } from '../../../shared/services/videoProcessingJobsService';
import Button from '../../../shared/components/ui/Button';

const UploadPage: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();
  const location = window.location;
  const { currentOrganization } = useOrg();
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);
  const orgId = currentOrganization?.id || orgIdFromPath || '';

  const [video, setVideo] = useState<Video | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);
  const [jobCreated, setJobCreated] = useState(false);

  const uploadAbortControllerRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load video details
  useEffect(() => {
    const loadVideo = async () => {
      if (!videoId) {
        setError('Video ID is required');
        setIsLoading(false);
        return;
      }

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setError('Not authenticated');
          setIsLoading(false);
          return;
        }

        const result = await videosService.getVideoById(videoId);
        if (!result.success || !result.data) {
          setError('Video not found');
          setIsLoading(false);
          return;
        }

        // Validate org access
        if (result.data.org_id !== orgId) {
          setError('Access denied');
          setIsLoading(false);
          return;
        }

        setVideo(result.data);

        // Check for existing upload session
        const existingSession = loadUploadSession(videoId);
        if (existingSession) {
          // Resume option could be shown here
          console.log('Found existing upload session:', existingSession);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load video');
      } finally {
        setIsLoading(false);
      }
    };

    loadVideo();
  }, [videoId, orgId]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setProgress(null);
      setUploadComplete(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
      setError(null);
      setProgress(null);
      setUploadComplete(false);
    }
  };

  const handleUpload = async () => {
    if (!file || !videoId) {
      setError('Please select a file');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadComplete(false);
    uploadAbortControllerRef.current = new AbortController();

    try {
      await uploadFile(
        file,
        videoId,
        {
          onProgress: (progressData) => {
            setProgress(progressData);
          },
          onComplete: () => {
            setUploadComplete(true);
            setIsUploading(false);
            clearUploadSession(videoId);
          },
          onError: (err) => {
            setError(err.message);
            setIsUploading(false);
          },
        }
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    if (uploadAbortControllerRef.current) {
      uploadAbortControllerRef.current.abort();
    }
    setIsUploading(false);
    setProgress(null);
    if (videoId) {
      clearUploadSession(videoId);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatTime = (seconds?: number): string => {
    if (!seconds || !isFinite(seconds)) return '--';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const formatSpeed = (bytesPerSecond?: number): string => {
    if (!bytesPerSecond || !isFinite(bytesPerSecond)) return '--';
    return `${formatFileSize(bytesPerSecond)}/s`;
  };

  const handleProcessVideo = async () => {
    if (!videoId) {
      setProcessError('Video ID is required');
      return;
    }

    setIsProcessing(true);
    setProcessError(null);

    try {
      const result = await videoProcessingJobsService.processVideo(videoId);
      
      if (!result.success) {
        setProcessError(result.error || 'Failed to start video processing');
        return;
      }

      setJobCreated(true);
    } catch (err) {
      setProcessError(err instanceof Error ? err.message : 'Failed to start video processing');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !video) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => navigate(`/o/${orgId}/capture`)} variant="primary">
            Return to Capture
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Minimal Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Upload Video</h1>
            {video && (
              <p className="text-sm text-gray-600 mt-1">{video.file_name}</p>
            )}
          </div>
          <button
            onClick={() => navigate(`/o/${orgId}/capture`)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {uploadComplete ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="text-green-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Upload Complete!</h2>
            {jobCreated ? (
              <>
                <p className="text-gray-600 mb-6">
                  Your video has been uploaded successfully and processing has been started. Workers will now process the file (transcode, generate thumbnails, etc.).
                </p>
                <div className="flex justify-center space-x-4">
                  <Button
                    onClick={() => navigate(`/o/${orgId}/capture`)}
                    variant="primary"
                  >
                    Return to Capture
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-6">
                  Your video has been uploaded successfully. Click "Process Video" to start transcoding and generating thumbnails.
                </p>
                {processError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{processError}</p>
                  </div>
                )}
                <div className="flex justify-center space-x-4">
                  <Button
                    onClick={handleProcessVideo}
                    variant="primary"
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'Processing...' : 'Process Video'}
                  </Button>
                  <Button
                    onClick={() => navigate(`/o/${orgId}/capture`)}
                    variant="secondary"
                  >
                    Return to Capture
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* File Selection */}
            {!file && !isUploading && (
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className="bg-white rounded-lg shadow-sm border-2 border-dashed border-gray-300 p-12 text-center hover:border-blue-400 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Drop your video file here
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  or click to browse
                </p>
                <p className="text-xs text-gray-500">
                  Supports large files (10GB+) using multipart upload
                </p>
              </div>
            )}

            {/* File Info */}
            {file && !isUploading && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{file.name}</h3>
                      <p className="text-sm text-gray-600">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setFile(null);
                      setProgress(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="flex justify-end space-x-3">
                  <Button onClick={handleCancel} variant="secondary">
                    Cancel
                  </Button>
                  <Button onClick={handleUpload} variant="primary">
                    Start Upload
                  </Button>
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {isUploading && progress && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-medium text-gray-900">Uploading...</h3>
                    <span className="text-sm font-medium text-blue-600">
                      {progress.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-blue-600 h-full transition-all duration-300 ease-out"
                      style={{ width: `${progress.percentage}%` }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Uploaded</p>
                    <p className="font-medium text-gray-900">
                      {formatFileSize(progress.uploadedBytes)} / {formatFileSize(progress.totalBytes)}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Speed</p>
                    <p className="font-medium text-gray-900">{formatSpeed(progress.speed)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Time Remaining</p>
                    <p className="font-medium text-gray-900">{formatTime(progress.eta)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Parts</p>
                    <p className="font-medium text-gray-900">
                      {progress.uploadedParts} / {progress.totalParts}
                    </p>
                  </div>
                </div>

                {progress.currentPart && (
                  <div className="mt-4 text-sm text-gray-600">
                    Uploading part {progress.currentPart} of {progress.totalParts}...
                  </div>
                )}

                <div className="mt-6 flex justify-end space-x-3">
                  <Button onClick={handleCancel} variant="secondary">
                    Cancel Upload
                  </Button>
                </div>

                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Important:</strong> Do not close this tab while uploading. Your progress is saved and you can resume if needed.
                  </p>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800">Upload Error</p>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default UploadPage;

